import activityMenu from "./insights/activity-menu.js";

/**
 * Sent on window to an activity's table, {activity, row}: bring that row forward (open, on screen,
 * focused) in an activity that is already mounted, which no &row= reaches since it mounted
 * (insights-activity-table.js).
 */
export const BRING_ROW_FORWARD_EVENT = "insights-bring-row-forward";

/**
 * Heard on window from an activity's table, {activity, row}: the reader opened a row (its id) or closed
 * it (null), which the address names as &row= (D45, D76).
 */
export const ROW_EVENT = "insights-activity-row";

// How long a chip that already covers a typed term stays lit, as long as a row a link brought forward
// (insights-activity-table.js): long enough to find, short enough not to read as a state of the chip.
const POINT_MS = 2500;

// The brand term lists a chip may be lit in: Sort my searches' card (ia_pointAtTerm()).
const TERM_LISTS = ["sort"];

// Every component that can change what a menu figure counts is a site's (sites.*: the activities, the
// drawer, the topic editor, the brand terms and connection dialogs); the page's own parts (the bug report,
// the workspace switcher, Ask AI) never can. Of the site's, the Actions view's own and Stats' never do
// (changesCards()).
const SITE_COMPONENTS = "sites.";
const ACTIONS_COMPONENT = "sites.insights.actions-view";
const STATS_COMPONENT = "sites.analytics";

/**
 * What a component may ask the server without changing anything a menu figure counts: its reads (an
 * activity's first load, a table row's details), its polls, a dialog opened or checked before its press,
 * Above you's read of Google queued (checkGoogleNow, which no figure shows), and the visit's end. Anything
 * else may have (a Done, a stage, a topic, a target, brand terms, a goal), so the menu's figures are asked
 * for again when the menu next opens or another activity opens: a call missing here costs one small
 * request, never a figure that misses what was just done (Ali, 9 Oct).
 */
export const READ_ONLY_CALLS = new Set(["load", "__lazyLoad", "activityTableExpand", "checkGoogleNow", "pageTo", "poll", "checkRegroup", "pollRun", "chooseFor", "checkChoice", "leave"]);

/**
 * The events a component hears (Livewire's __dispatch, the event's name first) that only read or open. An
 * event is no echo of a call counted elsewhere: the browser sends some as the change itself, with no call
 * before them (a toast's Undo), so any other event counts.
 */
export const READ_ONLY_EVENTS = new Set(["open-draft-article", "draft-article-load", "open-site-topic-editor", "regroup-site-topics", "insights-activity-left"]);

const readsOnly = (call) => (call?.method === "__dispatch" ? READ_ONLY_EVENTS.has(call.params?.[0]) : READ_ONLY_CALLS.has(call?.method));

/**
 * The fields a component sends that save nothing until a call does, or only open, page, search or select
 * within its own parts (Sort my searches' paged and bulk lists among them).
 */
export const READ_ONLY_UPDATES = new Set(["isGoalOpen", "measure", "show", "row", "newBrandTerm", "newTopicName", "choiceTopicId", "pages", "selected", "allPages", "topicFind", "lastOpenTopicId"]);

/**
 * Whether a Livewire request a component sends (its calls, and the fields it updates) may change what a
 * menu figure counts: a site component's, but neither the Actions view's own nor Stats', whose reads
 * change nothing. The page's own parts send fields as it starts (the bug report its user agent), which
 * would otherwise mark every landing's figures as changed.
 */
export function changesCards(componentName, commit) {
    if (!String(componentName).startsWith(SITE_COMPONENTS) || componentName === ACTIONS_COMPONENT || componentName === STATS_COMPONENT) {
        return false;
    }

    const calls = commit?.calls ?? [];
    const updates = Object.keys(commit?.updates ?? {});

    return calls.some((call) => !readsOnly(call)) || updates.some((path) => !READ_ONLY_UPDATES.has(path.split(".")[0]));
}

// The components a visit leaves something in, which its "left" ends: an opportunity activity's finished
// rows and their Undos (OpportunityActivity::leave()), and Win back clicks' folded searches and their Undos
// (WinBackClicks::leave()). Each must answer leave().
export const VISIT_HOLDERS = {
    "sites.insights.activities.write-something-new": "rows",
    "sites.insights.activities.improve-a-page": "rows",
    "sites.insights.activities.win-back-clicks": "searches",
};

const holdsAny = (value) => (Array.isArray(value) ? value.length > 0 : value !== null && typeof value === "object" && Object.keys(value).length > 0);

/**
 * Whether a component holds something this visit left that its "left" ends, read through `get` (a
 * field's name to its value): finished rows, or the Undo of a Done, a set-aside or a Move to topic (P5's
 * `moved`, which replaced the Topic parts' stage Undos) that its own Livewire actions made, or a search Win
 * back clicks folded (with an Undo or, for a search already a target keyword, without). Most visits leave
 * nothing, and then the activity is not asked at all.
 */
export function holdsVisit(componentName, get) {
    switch (VISIT_HOLDERS[componentName]) {
        case "rows": {
            const rows = get("answer")?.rows;

            return holdsAny(get("targeted")) || holdsAny(get("setAside")) || holdsAny(get("moved")) || (Array.isArray(rows) && rows.some((row) => row?.isFinished === true));
        }
        case "searches":
            // finishedThisVisit counts every fold not undone, with an Undo (targeted, setAside) or without.
            return get("finishedThisVisit") > 0;
        default:
            return false;
    }
}

/**
 * The Actions view's browser half (Livewire\Sites\Insights\ActionsView; D96 of
 * documentation/pr-plans/insights-simplify-plan.md, P7): opening an activity from the activity menu, a
 * link or Back, the menu's figures, and the light on a brand term chip that already covers a typed term.
 *
 * An activity opened shows at once, before any round trip: this sets $wire.activity, which the panes show
 * from and Livewire writes to the address, and only then asks the server to mount and remember it. Focus
 * follows the reader (documentation/accessibility.md §2): into the activity's heading once it is there,
 * onto its pane while it mounts, and back on the link they left from when Back returns them.
 *
 * A row the address names (&row=) is the open activity's open row: set as the reader opens and closes
 * rows, and by a link that opens an activity at a row. Opening another activity drops it from the address
 * at once, and ends the visit to the one left (ia_leave()).
 *
 * The menu's figures are asked for counted now once the page is up (the page drew the ones this person last
 * saw), and after that only once a request a site component sent since may have changed them, as the menu
 * opens or another activity opens (ia_countIfStale()); background work polls by itself (ActionsView).
 */
export default function InsightsActions() {
    return {
        // The activity menu's own state and keys (insights/activity-menu.js).
        ...activityMenu(),

        // Whether a request sent since the figures were last counted may have changed what they count
        // (changesCards()), and the watch's way out.
        ia_cardsStale: false,
        ia_unwatch: null,

        // The chip ia_pointAtTerm() lit, and the timer that puts it out.
        ia_pointed: null,
        ia_pointTimer: null,

        // The activities asked to mount and not mounted yet, so one is never asked twice.
        ia_mounting: [],

        init() {
            this.ia_unwatch = window.Livewire?.hook("commit", ({ component, commit }) => {
                if (changesCards(component.name, commit)) {
                    this.ia_cardsStale = true;
                }
            }) ?? null;

            // The figures counted now, once the page is up: its first response drew the last ones seen.
            requestAnimationFrame(() => this.ia_loadCards());
        },

        destroy() {
            this.ia_unwatch?.();
        },

        /**
         * An activity opened in place (insights-open-activity, {activity, origin, row, returnTo}): a menu item,
         * a key figure on Stats, a link to one of its rows, or Back and Forward (insights-views.js, which has
         * already written the address and shows the activities).
         *
         * An activity already mounted is shown as it was left, and nothing is read again (D76): Back puts
         * focus on the link the reader left it from (returnTo), and a row is brought forward by the table
         * itself. One never mounted is mounted and recorded as this person's last, with the row handed over
         * as it mounts (&row=). Focus moves to the activity from the link that is no longer on screen. For one
         * mounted only now, answers the mount's promise.
         */
        ia_open({ activity, origin = null, row = null, returnTo = null } = {}) {
            const pane = this.ia_pane(activity);

            if (!pane) {
                return;
            }

            const id = typeof row === "string" && row !== "" ? row : null;
            const left = this.$wire.activity;
            const isShown = left === activity;

            if (!isShown) {
                this.ia_leave(left);
                this.ia_countIfStale();
            }

            if ((this.$wire.opened ?? []).includes(activity)) {
                if (id !== null) {
                    this.$wire.row = id;
                }

                if (!isShown) {
                    if (id === null) {
                        this.$wire.row = "";
                    }

                    this.$wire.activity = activity;
                    this.$wire.pick(activity, id);
                }

                this.$nextTick(() => requestAnimationFrame(() => (id === null ? this.ia_return(activity, origin, returnTo) : this.ia_bringForward(activity, id, pane))));

                return;
            }

            const focus = () => this.ia_focusActivity(activity, origin);

            this.$wire.row = id ?? "";
            this.$wire.activity = activity;
            this.$nextTick(() => requestAnimationFrame(focus));

            return this.ia_mount(activity, id).then(() => this.$nextTick(focus));
        },

        /** Asks the server to mount an activity and remember it, once while the request runs. */
        ia_mount(activity, row = null) {
            this.ia_mounting = [...this.ia_mounting, activity];

            return this.$wire.pick(activity, row).finally(() => {
                this.ia_mounting = this.ia_mounting.filter((key) => key !== activity);
            });
        },

        /**
         * A row of a mounted activity brought forward by its table. Focus first moves onto the pane (it
         * takes focus by script), off the link that is no longer on screen, so the table may move it on
         * to the row.
         */
        ia_bringForward(activity, row, pane, highlight = true) {
            if (this.$wire.activity !== activity) {
                return;
            }

            pane.focus({ preventScroll: true });
            window.dispatchEvent(new CustomEvent(BRING_ROW_FORWARD_EVENT, { detail: { activity, row, highlight } }));
        },

        /**
         * Back in a mounted activity, as the reader left it: focus on the link they left from, where it
         * still is in the pane, which also brings that place back on screen (B7). Without one (after a
         * reload, or Stats reached from the menu), on the row they had open, brought back with no highlight;
         * else, as any opening, on its heading.
         */
        ia_return(activity, origin, returnTo) {
            if (this.$wire.activity !== activity) {
                return;
            }

            if (returnTo?.isConnected && this.ia_pane(activity)?.contains(returnTo)) {
                returnTo.focus();

                return;
            }

            const row = this.$wire.row;

            if (typeof row === "string" && row !== "") {
                this.ia_bringForward(activity, row, this.ia_pane(activity), false);

                return;
            }

            this.ia_focusActivity(activity, origin);
        },

        /** The reader opened a row of the open activity, or closed it: the address names it (&row=). */
        ia_rowChanged({ activity, row = null } = {}) {
            if (this.$wire.activity !== activity) {
                return;
            }

            this.$wire.row = typeof row === "string" ? row : "";
        },

        /**
         * The visit to an activity ended, as another opened: it drops its slim rows and their Undos (S37.1,
         * OpportunityActivity::leave(), WinBackClicks::leave()). Asked of the components in that activity's
         * pane alone, each in its own request, and only of one that holds something the visit left: never the
         * other activities, whose whole state a page-wide event sent up with it.
         */
        ia_leave(key) {
            const pane = this.ia_pane(key);

            if (!pane) {
                return;
            }

            for (const component of window.Livewire?.all?.() ?? []) {
                if (pane.contains(component.el) && holdsVisit(component.name, (field) => component.$wire[field])) {
                    component.$wire.leave(key);
                }
            }
        },

        /**
         * The menu's figures asked for again, only once a request sent since may have changed what they
         * count (the menu opening, another activity opening); otherwise nothing is sent.
         */
        ia_countIfStale() {
            if (this.ia_cardsStale) {
                this.ia_loadCards();
            }
        },

        ia_loadCards() {
            this.ia_cardsStale = false;
            this.$wire.loadCards();
        },

        /**
         * Stats gave way to the activities (insights-views.js): an activity the page did not mount, because it
         * opened on Stats, mounts now (ActionsView::mount()), and the figures are asked for if stale.
         */
        ia_viewShown(detail) {
            if (detail?.view !== "actions") {
                return;
            }

            const activity = this.$wire.activity;

            if (!(this.$wire.opened ?? []).includes(activity) && !this.ia_mounting.includes(activity)) {
                this.ia_mount(activity).then(() => this.$nextTick(() => this.ia_focusActivity(activity, null)));
            }

            this.ia_countIfStale();
        },

        /**
         * Focus into an open activity: its heading (the seat's activity-heading, which takes focus by
         * script), or, while the activity is still mounting, its pane. Only while the reader is still
         * waiting on it, with focus on the link they pressed, on the pane, or nowhere: never once they
         * have moved on to another activity, or focused anything else meanwhile.
         */
        ia_focusActivity(key, origin) {
            if (this.$wire.activity !== key) {
                return;
            }

            const pane = this.ia_pane(key);
            const active = document.activeElement;

            // Focus already somewhere in the activities stays there; on another view's pane, about to hide (Back from
            // a guide's section), or nowhere, it moves to the heading.
            if (active && active !== document.body && active !== origin && active !== pane && this.$root.contains(active)) {
                return;
            }

            (document.getElementById(`insights-activity-${key}-heading`) ?? pane)?.focus();
        },

        ia_pane(key) {
            return this.$root.querySelector(`[data-insights-activity-pane="${key}"]`);
        },

        // ------------------------------------------------------------- the menu's background work

        /**
         * The × on the menu's AI grouping line: hidden until that run ends (ActionsView::hideWork()). The line
         * leaves with its ×, so focus moves to the item on screen in the menu, never to the page
         * (documentation/accessibility.md §2).
         */
        ia_hideWork() {
            this.$wire.hideWork().then(() => this.$nextTick(() => document.querySelector('#insights-activity-menu-panel a[aria-current="page"], #insights-activity-menu-button')?.focus()));
        },

        /**
         * An Add whose term a chip already covers (ActionsView::BRAND_TERM_COVERED_EVENT, {list, term}), on
         * Sort my searches' card: that chip is lit for a moment, so the reader sees which one covers it,
         * while the line under the field says so in words. The light is a ring, which reduced motion shows
         * and takes away at once (the chip's own classes, data-covering).
         */
        ia_pointAtTerm({ list, term } = {}) {
            if (!TERM_LISTS.includes(list)) {
                return;
            }

            const chip = [...this.$root.querySelectorAll(`[data-brand-term-list="${list}"] [data-term-chip]`)].find((element) => element.dataset.term === term);

            if (!chip) {
                return;
            }

            clearTimeout(this.ia_pointTimer);
            this.ia_pointed?.removeAttribute("data-covering");
            this.ia_pointed = chip;
            chip.setAttribute("data-covering", "");
            this.ia_pointTimer = setTimeout(() => {
                chip.removeAttribute("data-covering");
                this.ia_pointed = null;
            }, POINT_MS);
        },
    };
}
